import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatMoney, formatDate } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowRight, User, Gavel, FileText, Clock, PencilSimple, UserPlus, Plus, Receipt, Buildings, Scales } from '@phosphor-icons/react';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  filed: 'bg-indigo-100 text-indigo-700',
  in_hearing: 'bg-orange-100 text-orange-700',
  judgment: 'bg-purple-100 text-purple-700',
  execution: 'bg-violet-100 text-violet-700',
  closed: 'bg-emerald-100 text-emerald-700',
  settled: 'bg-teal-100 text-teal-700',
};

const ACTION_LABELS = {
  note: 'ملاحظة', hearing: 'جلسة', filing: 'رفع دعوى', court_visit: 'حضور جلسة',
  verdict: 'صدور حكم', execution: 'إجراء تنفيذ', referral: 'إحالة',
  assignment: 'إسناد', status_change: 'تغيير الحالة', other: 'أخرى',
};

export default function LegalClaimDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [claim, setClaim] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [settings, setSettings] = useState({ statuses: [], claim_types: [] });
  const [users, setUsers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [tlOpen, setTlOpen] = useState(false);
  const [tl, setTl] = useState({ action_type: 'note', title: '', description: '', hearing_date: '', court_name: '' });

  const load = async () => {
    const [c, t] = await Promise.all([
      api.get(`/legal/claims/${id}`),
      api.get(`/legal/claims/${id}/timeline`),
    ]);
    setClaim(c.data);
    setTimeline(t.data);
    setEditData({
      status: c.data.status, priority: c.data.priority || 'medium',
      court_name: c.data.court_name || '', case_no: c.data.case_no || '',
      lawyer_name: c.data.lawyer_name || '', lawyer_phone: c.data.lawyer_phone || '',
      notes: c.data.notes || '',
    });
  };

  useEffect(() => {
    load();
    api.get('/legal/settings').then(({ data }) => setSettings(data));
    api.get('/legal/users').then(({ data }) => setUsers(data));
  }, [id]); // eslint-disable-line

  if (!claim) return <div className="text-slate-400">جارٍ التحميل...</div>;

  const statusLabel = (code) => settings.statuses.find(s => s.code === code)?.label_ar || code;
  const invoices = claim.snapshot?.invoices || [];
  const contract = claim.snapshot?.contract || {};
  const tenant = claim.snapshot?.tenant || {};

  const doAssign = async () => {
    if (!assignee) { toast.error('اختر موظفاً'); return; }
    try {
      await api.post(`/legal/claims/${id}/assign`, { assigned_to: assignee });
      toast.success('تم إسناد المطالبة');
      setAssignOpen(false); load();
    } catch { toast.error('فشل الإسناد'); }
  };

  const doEdit = async () => {
    try {
      await api.put(`/legal/claims/${id}`, editData);
      toast.success('تم التحديث');
      setEditOpen(false); load();
    } catch { toast.error('فشل التحديث'); }
  };

  const addTimeline = async () => {
    try {
      await api.post(`/legal/claims/${id}/timeline`, { ...tl, action_date: new Date().toISOString() });
      toast.success('تمت إضافة الإجراء');
      setTl({ action_type: 'note', title: '', description: '', hearing_date: '', court_name: '' });
      setTlOpen(false); load();
    } catch { toast.error('فشل الإضافة'); }
  };

  return (
    <div className="space-y-6" data-testid="legal-claim-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => nav('/legal/claims')} data-testid="back-btn">
            <ArrowRight size={16} className="ms-1" /> رجوع
          </Button>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#1E3A5F] mb-1">Legal Claim</div>
            <h1 className="text-3xl font-bold text-slate-900 font-mono">{claim.claim_no}</h1>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLES[claim.status] || 'bg-slate-100'}`}>{statusLabel(claim.status)}</span>
              <span className="text-sm text-slate-500">·</span>
              <span className="text-sm text-slate-700">{claim.title}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-claim-btn">
            <PencilSimple className="ms-1" size={14} /> تعديل
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)} data-testid="assign-btn">
            <UserPlus className="ms-1" size={14} /> إسناد
          </Button>
          <Button className="bg-slate-900 text-white" size="sm" onClick={() => setTlOpen(true)} data-testid="add-timeline-btn">
            <Plus className="ms-1" size={14} /> إجراء جديد
          </Button>
        </div>
      </div>

      {/* Financial snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="text-xs text-slate-500 mb-1">إجمالي العقد</div>
          <div className="text-xl font-bold text-slate-900 tabular-nums">{formatMoney(claim.contract_total)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="text-xs text-slate-500 mb-1">المسدد</div>
          <div className="text-xl font-bold text-emerald-700 tabular-nums">{formatMoney(claim.total_paid)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="text-xs text-slate-500 mb-1">المتبقي</div>
          <div className="text-xl font-bold text-red-700 tabular-nums">{formatMoney(claim.total_remaining)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="text-xs text-slate-500 mb-1">المطالب به</div>
          <div className="text-xl font-bold text-[#1E3A5F] tabular-nums">{formatMoney(claim.claim_amount)}</div>
        </Card>
      </div>

      {/* Meta */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-3">
            <User className="text-slate-500" size={16} /><h3 className="font-semibold text-sm text-slate-900">الأطراف</h3>
          </div>
          <div className="text-sm space-y-1.5">
            <div><span className="text-slate-500">المستأجر: </span><span className="text-slate-900">{claim.tenant_name || '—'}</span></div>
            <div><span className="text-slate-500">الجوال: </span><span className="text-slate-900">{tenant.phone || '—'}</span></div>
            <div><span className="text-slate-500">الهوية: </span><span className="text-slate-900">{tenant.national_id || '—'}</span></div>
            <div><span className="text-slate-500">العقار: </span><span className="text-slate-900">{claim.property_name || '—'}</span></div>
            <div><span className="text-slate-500">الفرع: </span><span className="text-slate-900">{claim.branch || '—'}</span></div>
          </div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="text-slate-500" size={16} /><h3 className="font-semibold text-sm text-slate-900">العقد</h3>
          </div>
          <div className="text-sm space-y-1.5">
            <div><span className="text-slate-500">رقم العقد: </span><span className="text-slate-900 font-mono">{claim.contract_no || '—'}</span></div>
            <div><span className="text-slate-500">من: </span><span className="text-slate-900">{contract.start_date || '—'}</span></div>
            <div><span className="text-slate-500">إلى: </span><span className="text-slate-900">{contract.end_date || '—'}</span></div>
            <div><span className="text-slate-500">قيمة الإيجار: </span><span className="text-slate-900 tabular-nums">{formatMoney(contract.rent_amount)}</span></div>
          </div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-3">
            <Gavel className="text-slate-500" size={16} /><h3 className="font-semibold text-sm text-slate-900">القضية</h3>
          </div>
          <div className="text-sm space-y-1.5">
            <div><span className="text-slate-500">المحكمة: </span><span className="text-slate-900">{claim.court_name || '—'}</span></div>
            <div><span className="text-slate-500">رقم الدعوى: </span><span className="text-slate-900 font-mono">{claim.case_no || '—'}</span></div>
            <div><span className="text-slate-500">الجلسة القادمة: </span><span className="text-slate-900">{formatDate(claim.next_hearing_date)}</span></div>
            <div><span className="text-slate-500">المحامي: </span><span className="text-slate-900">{claim.lawyer_name || '—'}</span></div>
            <div><span className="text-slate-500">الموظف المسؤول: </span><span className="text-slate-900">{claim.assigned_to_name || '—'}</span></div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList data-testid="claim-tabs">
          <TabsTrigger value="timeline" data-testid="tab-timeline">الإجراءات ({timeline.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">الفواتير المرتبطة ({invoices.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">الملاحظات</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            {timeline.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-8">لا توجد إجراءات بعد</div>
            ) : (
              <div className="relative border-r-2 border-slate-100 pr-6 space-y-4">
                {timeline.map(t => (
                  <div key={t.id} className="relative" data-testid={`tl-${t.id}`}>
                    <div className="absolute -right-[33px] top-1 w-4 h-4 rounded-full bg-white border-2 border-[#1E3A5F]" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-slate-100 rounded font-medium">{ACTION_LABELS[t.action_type] || t.action_type}</span>
                      <span className="text-sm font-semibold text-slate-900">{t.title}</span>
                      <span className="text-xs text-slate-400">· {formatDate(t.action_date || t.created_at)}</span>
                    </div>
                    {t.description && <div className="text-sm text-slate-600 mt-1 leading-loose">{t.description}</div>}
                    {t.hearing_date && <div className="text-xs text-orange-700 mt-1">موعد الجلسة: {formatDate(t.hearing_date)}</div>}
                    {t.user_name && <div className="text-xs text-slate-400 mt-1">بواسطة {t.user_name}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            {invoices.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-6">لا توجد فواتير</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs">
                    <tr>
                      <th className="p-2 text-start">رقم الدفعة</th>
                      <th className="p-2 text-start">الاستحقاق</th>
                      <th className="p-2 text-end">المبلغ</th>
                      <th className="p-2 text-end">المسدد</th>
                      <th className="p-2 text-end">المتبقي</th>
                      <th className="p-2 text-start">الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(i => (
                      <tr key={i.id} className="border-t border-slate-100">
                        <td className="p-2 font-mono">{i.payment_no}</td>
                        <td className="p-2">{formatDate(i.due_date)}</td>
                        <td className="p-2 text-end tabular-nums">{formatMoney(i.invoice_amount)}</td>
                        <td className="p-2 text-end tabular-nums text-emerald-700">{formatMoney(i.collected_amount)}</td>
                        <td className="p-2 text-end tabular-nums text-red-700">{formatMoney(i.remaining_amount)}</td>
                        <td className="p-2 text-xs">{i.payment_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-loose min-h-[6rem]">{claim.notes || 'لا توجد ملاحظات'}</div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader><DialogTitle>إسناد المطالبة</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm">الموظف المسؤول</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid="assignee-select"><SelectValue placeholder="اختر موظفاً" /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>إلغاء</Button>
            <Button className="bg-slate-900 text-white" onClick={doAssign} data-testid="assign-submit">إسناد</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader><DialogTitle>تعديل المطالبة</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm mb-1 block">الحالة</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {settings.statuses.map(s => <SelectItem key={s.code} value={s.code}>{s.label_ar}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm mb-1 block">المحكمة</Label>
                <Input value={editData.court_name} onChange={(e) => setEditData({ ...editData, court_name: e.target.value })} data-testid="edit-court" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">رقم الدعوى</Label>
                <Input value={editData.case_no} onChange={(e) => setEditData({ ...editData, case_no: e.target.value })} data-testid="edit-case-no" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">اسم المحامي</Label>
                <Input value={editData.lawyer_name} onChange={(e) => setEditData({ ...editData, lawyer_name: e.target.value })} data-testid="edit-lawyer" />
              </div>
              <div className="col-span-2">
                <Label className="text-sm mb-1 block">هاتف المحامي</Label>
                <Input value={editData.lawyer_phone} onChange={(e) => setEditData({ ...editData, lawyer_phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-sm mb-1 block">ملاحظات</Label>
                <Textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={3} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>إلغاء</Button>
            <Button className="bg-slate-900 text-white" onClick={doEdit} data-testid="edit-submit">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={tlOpen} onOpenChange={setTlOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader><DialogTitle>إضافة إجراء جديد</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm mb-1 block">نوع الإجراء</Label>
              <Select value={tl.action_type} onValueChange={(v) => setTl({ ...tl, action_type: v })}>
                <SelectTrigger data-testid="tl-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">ملاحظة</SelectItem>
                  <SelectItem value="hearing">تحديد جلسة</SelectItem>
                  <SelectItem value="filing">رفع دعوى</SelectItem>
                  <SelectItem value="court_visit">حضور جلسة</SelectItem>
                  <SelectItem value="verdict">صدور حكم</SelectItem>
                  <SelectItem value="execution">إجراء تنفيذ</SelectItem>
                  <SelectItem value="other">أخرى</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">العنوان</Label>
              <Input value={tl.title} onChange={(e) => setTl({ ...tl, title: e.target.value })} data-testid="tl-title" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">الوصف</Label>
              <Textarea value={tl.description} onChange={(e) => setTl({ ...tl, description: e.target.value })} rows={3} data-testid="tl-desc" />
            </div>
            {tl.action_type === 'hearing' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm mb-1 block">موعد الجلسة</Label>
                  <Input type="date" value={tl.hearing_date} onChange={(e) => setTl({ ...tl, hearing_date: e.target.value })} data-testid="tl-hearing-date" />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">المحكمة</Label>
                  <Input value={tl.court_name} onChange={(e) => setTl({ ...tl, court_name: e.target.value })} data-testid="tl-court" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTlOpen(false)}>إلغاء</Button>
            <Button className="bg-slate-900 text-white" onClick={addTimeline} data-testid="tl-submit">
              <Clock className="ms-1" size={14} /> إضافة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
